// The field notebook's records (HER-7, ARCH §28.11). PURE (ENG-1).
//
// Records live on the PLAYER (D-TF-135), not the item: the notebook item only opens the
// screen. This module owns their shape, their load-time sanitizer (M48.6), and — from
// M50.4 — the pure recording/eviction rules the host (boot.ts solo, server/session.ts MP)
// drives from a player action or a birth/death event.
import { HER } from '../config';
import { encodeGenome } from './genome';
import type { Entity } from '../entities/entity';

/** One recorded animal (§28.11). `genome` is base64 in its species' layout. */
export interface AnimalRecord {
  id: number;
  species: string;
  name?: string;
  genome: string;
  parents: [number, number];
  born: number;
  died?: number;
  cause?: 'old_age' | 'other';
}

/** One recorded genetic crop cell (§28.11). */
export interface CropRecord {
  x: number;
  y: number;
  z: number;
  crop: string;
  genome: string;
  harvests: number;
}

export interface Notebook {
  animals: AnimalRecord[];
  crops: CropRecord[];
}

const int = (v: unknown): v is number => Number.isInteger(v);
const str = (v: unknown): v is string => typeof v === 'string';

function animalRecord(v: unknown): AnimalRecord | null {
  if (typeof v !== 'object' || v === null) return null;
  const r = v as Record<string, unknown>;
  const parents = r['parents'];
  if (!int(r['id']) || !str(r['species']) || !str(r['genome']) || !int(r['born'])) return null;
  if (!Array.isArray(parents) || parents.length !== 2 || !int(parents[0]) || !int(parents[1])) return null;
  const out: AnimalRecord = { id: r['id'], species: r['species'], genome: r['genome'], parents: [parents[0], parents[1]], born: r['born'] };
  if (str(r['name'])) out.name = r['name'];
  if (int(r['died'])) out.died = r['died'];
  if (r['cause'] === 'old_age' || r['cause'] === 'other') out.cause = r['cause'];
  return out;
}

function cropRecord(v: unknown): CropRecord | null {
  if (typeof v !== 'object' || v === null) return null;
  const r = v as Record<string, unknown>;
  if (!int(r['x']) || !int(r['y']) || !int(r['z']) || !str(r['crop']) || !str(r['genome']) || !int(r['harvests'])) return null;
  return { x: r['x'], y: r['y'], z: r['z'], crop: r['crop'], genome: r['genome'], harvests: r['harvests'] };
}

/** A notebook from untrusted input: malformed records dropped, each list truncated at
 *  `HER.NOTEBOOK_MAX`, never a throw. Absent or not an object ⇒ undefined — exactly the
 *  pre-v4 absence, so a player who never carried a notebook round-trips unchanged. */
export function sanitizeNotebook(raw: unknown): Notebook | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const list = <T>(v: unknown, one: (x: unknown) => T | null): T[] =>
    (Array.isArray(v) ? v : []).map(one).filter((x): x is T => x !== null).slice(0, HER.NOTEBOOK_MAX);
  return { animals: list(r['animals'], animalRecord), crops: list(r['crops'], cropRecord) };
}

export function emptyNotebook(): Notebook {
  return { animals: [], crops: [] };
}

export function hasAnimal(nb: Notebook, id: number): boolean {
  return nb.animals.some((a) => a.id === id);
}

/** A record snapshot off a live, table-species entity (the caller has already
 *  confirmed `e.genome` exists — a species with no table records nothing).
 *  `tick` is the world's current tick; `born` is derived from it so re-recording
 *  the same animal later still reports a stable birth tick. */
export function animalRecordOf(e: Entity, tick: number): AnimalRecord | null {
  if (!e.genome) return null;
  const rec: AnimalRecord = {
    id: e.id, species: e.type, genome: encodeGenome(e.genome), parents: e.parents ?? [0, 0], born: tick - e.age,
  };
  if (e.name !== undefined) rec.name = e.name;
  return rec;
}

/** The dead record with the EARLIEST `died` tick ("the oldest dead record",
 *  E-56) — the eviction candidate when the list is at `HER.NOTEBOOK_MAX`. -1 if
 *  none is dead. */
function oldestDeadIndex(animals: readonly AnimalRecord[]): number {
  let idx = -1, min = Infinity;
  for (let i = 0; i < animals.length; i++) {
    const died = animals[i]!.died;
    if (died !== undefined && died < min) { min = died; idx = i; }
  }
  return idx;
}

/** Insert or refresh `rec` (matched by id). An existing DEATH mark survives a
 *  refresh — recording a living animal again never un-marks a dead one (the id
 *  cannot be reused, since a dead animal is removed, but a stale caller could
 *  otherwise resurrect the record). At the cap: refuse (E-56), unless a dead
 *  record can be evicted for it — that keeps the count at the cap, not over it. */
export function upsertAnimal(nb: Notebook, rec: AnimalRecord): { notebook: Notebook; ok: boolean } {
  const i = nb.animals.findIndex((a) => a.id === rec.id);
  if (i >= 0) {
    const existing = nb.animals[i]!;
    const animals = [...nb.animals];
    animals[i] = existing.died !== undefined ? { ...rec, died: existing.died, cause: existing.cause } : rec;
    return { notebook: { ...nb, animals }, ok: true };
  }
  if (nb.animals.length < HER.NOTEBOOK_MAX) return { notebook: { ...nb, animals: [...nb.animals, rec] }, ok: true };
  const dead = oldestDeadIndex(nb.animals);
  if (dead < 0) return { notebook: nb, ok: false };
  const animals = [...nb.animals];
  animals.splice(dead, 1, rec);
  return { notebook: { ...nb, animals }, ok: true };
}

/** Mark a RECORDED animal's death (any cause) — a no-op if it was never
 *  recorded, so a death event costs nothing for a player who never met it. */
export function markAnimalDeath(nb: Notebook, id: number, died: number, cause: 'old_age' | 'other'): Notebook {
  const i = nb.animals.findIndex((a) => a.id === id);
  if (i < 0) return nb;
  const animals = [...nb.animals];
  animals[i] = { ...animals[i]!, died, cause };
  return { ...nb, animals };
}

/** Insert or refresh the crop record at (x,y,z): a fresh cell records at
 *  `harvests: 1`; recording the SAME cell again (its genome may have changed —
 *  a replant) bumps the count. Crops have no death state, so there is no
 *  eviction path at the cap (E-56 only names one, for animals) — full is full. */
export function upsertCrop(nb: Notebook, x: number, y: number, z: number, crop: string, genome: string): { notebook: Notebook; ok: boolean } {
  const i = nb.crops.findIndex((c) => c.x === x && c.y === y && c.z === z);
  if (i >= 0) {
    const crops = [...nb.crops];
    crops[i] = { x, y, z, crop, genome, harvests: crops[i]!.harvests + 1 };
    return { notebook: { ...nb, crops }, ok: true };
  }
  if (nb.crops.length >= HER.NOTEBOOK_MAX) return { notebook: nb, ok: false };
  return { notebook: { ...nb, crops: [...nb.crops, { x, y, z, crop, genome, harvests: 1 }] }, ok: true };
}
